import React, { createContext, useState, useEffect, useContext } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import * as ROUTES from '../config/routes'

const AuthContext = createContext()

const AuthProvider = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [token, setToken] = useState(null)
  const [roles, setRoles] = useState([])
  const [lastVisitedPage, setLastVisitedPage] = useState(null)
  const location = useLocation()
  const navigate = useNavigate()

  const [isAdmin, setIsAdmin] = useState(false)
  const [isSubscriber, setIsSubscriber] = useState(false)

  useEffect(() => {
    const storedToken = localStorage.getItem('token')
    const storedIsLoggedIn = localStorage.getItem('isLoggedIn') === 'true'
    const storedRoles = JSON.parse(localStorage.getItem('roles'))
    const storedExpiredAt = localStorage.getItem('expired-at')

    if (storedToken) {
      setToken(storedToken)
      setIsLoggedIn(storedIsLoggedIn)
      setRoles(storedRoles)

      setIsAdmin(storedRoles?.includes('ROLE_ADMIN') || false)
      setIsSubscriber(storedRoles?.includes('ROLE_SUBSCRIBER') || false)

      checkTokenExpiration(storedToken, storedExpiredAt)
    }

    const excludedPages = [
      ROUTES.LOGIN_PATH,
      ROUTES.REGISTER_PATH,
      ROUTES.REGISTER_CONFIRMATION_PATH,
      ROUTES.FORGOTTEN_PASSWORD_REQUEST_PATH,
      ROUTES.FORGOTTEN_PASSWORD_PATH,
      ROUTES.REGISTER_VALIDATION_PATH,
    ]

    if (!excludedPages.some((path) => new RegExp(`^${path.replace(/:[^/]+/g, '[^/]+')}$`).test(location.pathname))) {
      setLastVisitedPage(location.pathname)
    }
  }, [location])

  const checkTokenExpiration = async (storedToken, storedExpiredAt) => {
    if (!storedExpiredAt) return

    const expirationDate = new Date(storedExpiredAt)
    const now = new Date()
    const diffInHours = (expirationDate - now) / (1000 * 60 * 60)

    if (diffInHours <= 24) {
      try {
        const response = await fetch('/api/token/refresh', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${storedToken}`,
          },
          body: JSON.stringify({ token: storedToken }),
        })

        if (!response.ok) {
          throw new Error('Token refresh failed')
        }

        const data = await response.json()
        handleLogin(data)
      } catch (error) {
        console.error('Token refresh error:', error)
        handleLogout()
      }
    }
  }

  const handleLogin = (tokenDatas) => {
    setIsLoggedIn(true)
    setToken(tokenDatas.token)
    setRoles(tokenDatas.roles || [])

    setIsAdmin(tokenDatas.roles?.includes('ROLE_ADMIN') || false)
    setIsSubscriber(tokenDatas.roles?.includes('ROLE_SUBSCRIBER') || false)

    localStorage.setItem('token', tokenDatas.token)
    localStorage.setItem('isLoggedIn', 'true')
    localStorage.setItem('roles', JSON.stringify(tokenDatas.roles))
    localStorage.setItem('expired-at', tokenDatas.expiredAt)
  }

  const logout = () => {
    setIsLoggedIn(false)
    setToken(null)
    setRoles([])
    setIsAdmin(false)
    setIsSubscriber(false)
    localStorage.removeItem('token')
    localStorage.setItem('isLoggedIn', 'false')
    localStorage.removeItem('roles')
    localStorage.removeItem('expired-at')
  }

  const handleLogout = (path = ROUTES.HOME_PATH) => {
    logout()
    navigate(path)
  }

  const authContextValue = {
    isLoggedIn,
    token,
    roles,
    isAdmin,
    isSubscriber,
    setToken,
    setRoles,
    setIsLoggedIn,
    handleLogin,
    logout,
    handleLogout,
    lastVisitedPage,
  }

  return (
    <AuthContext.Provider value={authContextValue}>
      {children}
    </AuthContext.Provider>
  )
}

const useAuth = () => useContext(AuthContext)

export { AuthProvider, useAuth, AuthContext }
